import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import type { UserDto } from "@/api/types";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { buttonVariants } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { twResolve } from "@/helpers/tw-resolve";
import { useSlug } from "@/hooks/useSlug";
import { useForbiddenPopupContext } from "@/providers/ForbiddenPopupProvider";
import { routes } from "@/routes";

import { UserFullName } from "../UserFullNameLink/UserFullNameLink";

export type UserProps<T> = {
  user: T;
  projectSlugOverride?: string;
  shouldNavigate?: boolean;
  size?: "sm" | "md";
  onClick?: () => void;
  renderSubtitle?: (user: T) => string | React.ReactNode | undefined;
  renderPostfix?: (user: T) => string | React.ReactNode | undefined;
};

export function User<T extends UserDto>({
  user,
  projectSlugOverride,
  shouldNavigate = true,
  size = "md",
  onClick,
  renderSubtitle,
  renderPostfix,
}: UserProps<T>): React.ReactNode {
  const slug = useSlug();
  const forbiddenContext = useForbiddenPopupContext();

  function renderProfileAction(children: React.ReactNode, inline = false): React.ReactNode {
    const className = inline ? "min-w-0 truncate" : "flex max-w-full items-center gap-space-sm overflow-hidden";

    if (shouldNavigate && user.canViewProfile) {
      return (
        <Link
          to={routes.users.details({ slug: projectSlugOverride || slug, id: user.id })}
          onClick={onClick}
          className={className}
        >
          {children}
        </Link>
      );
    }

    if (shouldNavigate) {
      return (
        <button
          type="button"
          onClick={() => {
            forbiddenContext.open();
            onClick?.();
          }}
          className={twResolve(buttonVariants({ variant: "ghostSecondary", type: "button" }), className)}
        >
          {children}
        </button>
      );
    }

    return children;
  }

  const avatar = <UserAvatar image={user.avatar} isUserDeleted={!!user.deletedAt} size={size} />;
  const name = <UserFullName user={user} size={size} />;

  const Content = (
    <>
      {renderPostfix ? renderProfileAction(avatar, true) : avatar}
      <span className={twJoin("flex flex-1 flex-col overflow-hidden", size === "sm" && "gap-space-xs")}>
        <span className="flex w-full flex-wrap items-center gap-x-space-xs gap-y-0">
          {renderPostfix ? renderProfileAction(name, true) : name}
          <span className="truncate">{renderPostfix?.(user)}</span>
        </span>
        {renderSubtitle && (
          <span
            className={twJoin(
              "truncate text-text-secondary",
              size === "sm" && "text-overline",
              size === "md" && "text-caption",
            )}
          >
            {renderSubtitle(user)}
          </span>
        )}
      </span>
    </>
  );

  if (shouldNavigate && !renderPostfix) {
    return renderProfileAction(Content);
  }

  return <article className="flex max-w-full items-center gap-space-sm overflow-hidden">{Content}</article>;
}

type UserTooltipProps = {
  fullName: string;
  address?: string;
};

export function UserTooltipContent({ fullName, address }: UserTooltipProps): React.ReactNode {
  return (
    <Stack align="start">
      <span className="text-left text-body-emphasis text-text-default-inverse">{fullName}</span>
      {address && <span className="text-left text-caption text-text-default-inverse">{address}</span>}
    </Stack>
  );
}
